import Link from 'next/link'
import Logo from './Logo'
import FooterSocialLinks from './FooterSocialLinks'
import { SITE_CONTAINER } from '@/lib/site-layout'

interface Category {
  id: number
  title: string
  slug: string
}

export type FooterTrendingTag = {
  tag: string
  slug: string
}

interface FooterProps {
  categories: Category[]
  trendingTags?: FooterTrendingTag[]
  weekTags?: FooterTrendingTag[]
  todayTags?: FooterTrendingTag[]
  showUserSubmissions?: boolean
}

type FooterLink = { label: string; href: string }
type FooterColumn = { title: string; links: FooterLink[] }

function tagLinks(items: FooterTrendingTag[]): FooterLink[] {
  return items.map((item) => ({
    label: `#${item.tag}`,
    href: `/haberleri/${item.slug}`,
  }))
}

function gridClass(count: number) {
  if (count <= 2) return 'grid-cols-2 md:grid-cols-2'
  if (count === 3) return 'grid-cols-2 md:grid-cols-3'
  if (count === 4) return 'grid-cols-2 md:grid-cols-4'
  return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'
}

export default function Footer({
  categories,
  trendingTags = [],
  weekTags = [],
  todayTags = [],
  showUserSubmissions = true,
}: FooterProps) {
  const primaryTags = trendingTags.slice(0, 8)
  const moreTags = trendingTags.slice(8, 16)
  // week/today yalnızca trending’de yoksa göster (yinelenen sütun olmasın)
  const weekOnly =
    weekTags.length > 0 && primaryTags.length === 0
      ? weekTags.slice(0, 8)
      : []
  const todayOnly =
    todayTags.length > 0 && primaryTags.length === 0 && weekOnly.length === 0
      ? todayTags.slice(0, 8)
      : []

  const columns: FooterColumn[] = [
    {
      title: 'Haber',
      links: [
        { label: 'Son Dakika', href: '/son-dakika' },
        ...categories.slice(0, 10).map((c) => ({
          label: c.title,
          href: `/${c.slug}`,
        })),
      ],
    },
    ...(primaryTags.length > 0
      ? [{ title: 'Gündem', links: tagLinks(primaryTags) }]
      : []),
    ...(weekOnly.length > 0
      ? [{ title: 'Haftanın Gündemi', links: tagLinks(weekOnly) }]
      : []),
    ...(todayOnly.length > 0
      ? [{ title: 'Bugün', links: tagLinks(todayOnly) }]
      : []),
    ...(moreTags.length > 0
      ? [{ title: 'Etiketler', links: tagLinks(moreTags) }]
      : []),
    {
      title: 'Haber.com',
      links: [
        { label: 'Hava Durumu', href: '/hava-durumu' },
        { label: 'Canlı TV', href: '/canli-tv' },
        { label: 'Canlı Maçlar', href: '/spor/canli-maclar' },
        { label: 'Fikstür', href: '/spor/fikstur' },
        { label: 'Puan Durumu', href: '/spor/puan-durumu' },
        { label: 'Hakkımızda', href: '/hakkimizda' },
        { label: 'Künye', href: '/kunye' },
        { label: 'RSS', href: '/rss-adresleri' },
        { label: 'Kullanım Şartları', href: '/kullanim-kosullari' },
        { label: 'Gizlilik Politikası', href: '/gizlilik-politikasi' },
        { label: 'Çerez Politikası', href: '/cerez-politikasi' },
        { label: 'Kişisel Verilerin Korunması', href: '/kvkk' },
      ],
    },
  ]

  const topNav = [
    { label: 'Hakkımızda', href: '/hakkimizda' },
    { label: 'Reklam', href: '/reklam' },
    { label: 'İletişim', href: '/iletisim' },
    { label: 'Künye', href: '/kunye' },
    ...(showUserSubmissions
      ? [{ label: 'Haber Gönder', href: '/haber-gonder' }]
      : []),
  ]

  return (
    <footer className="mt-10 bg-gray-100 text-gray-900 dark:bg-slate-950 dark:text-white sm:mt-14 md:mt-16">
      <div className="border-b border-gray-200 dark:border-slate-800">
        <div className={`${SITE_CONTAINER} py-5 sm:py-6`}>
          <div className="flex flex-col items-center gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex max-w-full flex-col items-center gap-2.5 text-center sm:flex-row sm:gap-3 sm:text-left">
              <Logo className="h-7 w-auto shrink-0 sm:h-8" />
              <span className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                © {new Date().getFullYear()} Haber.com · Tüm hakları saklıdır.
              </span>
            </div>
            <nav
              className="flex max-w-full flex-wrap justify-center gap-x-2.5 gap-y-1.5 text-xs sm:gap-x-3 sm:text-sm md:justify-end"
              aria-label="Kurumsal"
            >
              {topNav.map((item, idx) => (
                <span key={item.href} className="contents">
                  {idx > 0 ? (
                    <span
                      className="hidden text-gray-300 sm:inline dark:text-slate-600"
                      aria-hidden
                    >
                      ·
                    </span>
                  ) : null}
                  <Link
                    href={item.href}
                    className={`inline-flex min-h-6 items-center transition-colors hover:text-red-600 sm:whitespace-nowrap ${
                      item.href === '/haber-gonder'
                        ? 'font-semibold text-[#C02727] hover:text-[#a82020]'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {item.label}
                  </Link>
                </span>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <div className={`${SITE_CONTAINER} py-7 sm:py-8 md:py-9`}>
        <div
          className={`grid min-w-0 gap-x-4 gap-y-6 sm:gap-x-6 md:gap-8 ${gridClass(columns.length)}`}
        >
          {columns.map((column) => (
            <div key={column.title} className="min-w-0">
              <h4 className="mb-2.5 text-[13px] font-bold text-gray-900 dark:text-white sm:mb-3 sm:text-sm">
                {column.title}
              </h4>
              <ul className="space-y-0.5 text-[12px] leading-snug sm:space-y-1 sm:text-sm">
                {column.links.map((link) => (
                  <li key={`${column.title}-${link.href}-${link.label}`}>
                    <Link
                      href={link.href}
                      className="inline-flex min-h-6 items-center break-words py-0.5 text-gray-700 transition-colors hover:text-red-600 dark:text-gray-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-slate-800">
        <div
          className={`${SITE_CONTAINER} flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:py-6`}
        >
          <div>
            <h4 className="mb-2.5 text-xs font-bold uppercase tracking-wide text-gray-800 dark:text-white sm:mb-3">
              Bizi takip edin
            </h4>
            <FooterSocialLinks />
          </div>
          <p className="max-w-md text-xs leading-relaxed text-gray-500 dark:text-gray-400 sm:text-right sm:text-sm">
            Türkiye&apos;nin güncel haberleri, canlı yayınları ve gündemi
            Haber.com&apos;da.
          </p>
        </div>
      </div>
    </footer>
  )
}
